import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/Submit.css';
import '../styles/ICOListing.css';
import { getMyProject, updateProject, getSettings } from '../api/projects';
import { PROJECT_CATEGORIES, isValidYouTubeUrl } from '../constants/categories';


import Navbar from '../components/Navbar';

const STEPS = ['Info', 'Details', 'Tokenomics', 'Team', 'Socials & Ads', 'Confirm'];
const DISTRIB_COLORS = ['#F59E0B', '#34D399', '#3B82F6', '#8B5CF6', '#EC4899', '#10B981', '#F97316', '#06B6D4', '#A855F7', '#EF4444'];

export default function EditProject() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user, authLoading } = useAuth();
    const [step, setStep]       = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving]   = useState(false);
    const [saved, setSaved]     = useState(false);
    const [error, setError]     = useState('');
    const [form, setForm]       = useState(null);
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        if (authLoading) return;
        if (!user) { navigate('/connexion'); return; }
        Promise.all([getMyProject(slug), getSettings()]).then(([data, s]) => {
            setSettings(s);
            if (data.error) { navigate('/mon-compte'); return; }
            setForm({
                name:             data.name || '',
                email:            data.email || '',
                ticker:           data.ticker || '',
                type:             data.type || 'ico',
                chain:            data.chain || '',
                categorie:        data.categorie || '',
                website:          data.website || '',
                twitter:          data.twitter || '',
                telegram:         data.telegram || '',
                discord:          data.discord || '',
                whitepaper:       data.whitepaper || '',
                logo_url:         data.logo_url || '',
                description:      data.description || '',
                url_video:        data.url_video || '',
                contract_address: data.contract_address || '',
                launch_date:      data.launch_date || '',
                presale_start:    data.presale_start || '',
                presale_end:      data.presale_end || '',
                kyc:              data.kyc || '',
                kyc_url:          data.kyc_url || '',
                audit:            data.audit || '',
                audit_url:        data.audit_url || '',
                tokenomics:       data.tokenomics ? JSON.parse(data.tokenomics) : { 'Supply total': '', 'Hard Cap': '', 'Soft Cap': '', 'Vesting': '', devise: '', phases: [] },
                token_distribution: data.token_distribution ? JSON.parse(data.token_distribution) : [],
                roadmap:          data.roadmap ? JSON.parse(data.roadmap) : [],
                team:             data.team ? JSON.parse(data.team) : [],
                faq:              data.faq ? JSON.parse(data.faq) : [],
                active_promos:    data.active_promos || [],
                promoted_package: '',
                promoted_duration: '',
                update_comment:   '',
            });
            setLoading(false);
        });
    }, [slug, user]);

    const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
    const updateTokenomics = (field, value) => setForm(prev => ({ ...prev, tokenomics: { ...prev.tokenomics, [field]: value } }));

    const addPhase = () => setForm(prev => ({ ...prev, tokenomics: { ...prev.tokenomics, phases: [...prev.tokenomics.phases, { price: '', duration: '' }] } }));
    const updatePhase = (i, field, value) => { const phases = [...form.tokenomics.phases]; phases[i] = { ...phases[i], [field]: value }; setForm(prev => ({ ...prev, tokenomics: { ...prev.tokenomics, phases } })); };
    const removePhase = (i) => { const phases = form.tokenomics.phases.filter((_, idx) => idx !== i); setForm(prev => ({ ...prev, tokenomics: { ...prev.tokenomics, phases } })); };

    const addDistrib = () => update('token_distribution', [...form.token_distribution, { label: '', percent: '', color: DISTRIB_COLORS[form.token_distribution.length % DISTRIB_COLORS.length] }]);
    const updateDistrib = (i, field, value) => { const d = [...form.token_distribution]; d[i] = { ...d[i], [field]: value }; update('token_distribution', d); };
    const removeDistrib = (i) => update('token_distribution', form.token_distribution.filter((_, idx) => idx !== i));

    const addRoadmap = () => update('roadmap', [...form.roadmap, '']);
    const updateRoadmap = (i, value) => { const r = [...form.roadmap]; r[i] = value; update('roadmap', r); };
    const removeRoadmap = (i) => update('roadmap', form.roadmap.filter((_, idx) => idx !== i));

    const addTeam = () => update('team', [...form.team, { name: '', role: '', bio: '' }]);
    const updateTeam = (i, field, value) => { const team = [...form.team]; team[i] = { ...team[i], [field]: value }; update('team', team); };
    const removeTeam = (i) => update('team', form.team.filter((_, idx) => idx !== i));

    const addFaq = () => update('faq', [...form.faq, { q: '', a: '' }]);
    const updateFaq = (i, field, value) => { const faq = [...form.faq]; faq[i] = { ...faq[i], [field]: value }; update('faq', faq); };
    const removeFaq = (i) => update('faq', form.faq.filter((_, idx) => idx !== i));

    const isPromoActive = (type) => {
        if (!form?.active_promos) return false;
        return form.active_promos.some(p => p.promo_type === type && p.status === 'confirmed');
    };

    const handleSave = async () => {
        if (!form.categorie || !PROJECT_CATEGORIES.includes(form.categorie)) {
            setError('Category is required — go back to the Info step.');
            setStep(0);
            return;
        }
        if (form.url_video && !isValidYouTubeUrl(form.url_video)) {
            setError('The video URL must be a valid YouTube link.');
            setStep(1);
            return;
        }
        if (!form.update_comment || form.update_comment.trim().length < 10) {
            setError('Comment is required (min 10 characters) — describe the changes made.');
            return;
        }
        setSaving(true); setError('');
        const result = await updateProject(slug, form);
        if (result.success) setSaved(true);
        else setError(result.error || 'Error saving changes');
        setSaving(false);
    };

    if (loading) return <div className="app-wrap"><div style={{ padding: '4rem', textAlign: 'center', color: '#94a3b8' }}>Loading...</div></div>;

    if (saved) return (
        <div className="app-wrap">
            <nav className="nav"><div className="nav-logo" onClick={() => navigate('/')}>ONLINEICO</div></nav>
            <div className="submit-wrap">
                <div className="submit-card">
                    <div className="success-wrap">
                        <div className="success-icon">✅</div>
                        <div className="success-title">Project updated!</div>
                        <p className="success-text">Your project has been modified and will go back through validation before being published.</p>
                        <button className="btn-submit" style={{ marginTop: '2rem' }} onClick={() => navigate('/mon-compte')}>Back to my account</button>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="app-wrap">
            <Navbar />

            <div className="submit-wrap">
                <div style={{ marginBottom: '1.5rem' }}>
                    <button className="back-btn" onClick={() => navigate('/mon-compte')}>← My account</button>
                    <div style={{ color: '#F1F5F9', fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '1.5rem', marginTop: '8px' }}>
                        Edit — {form.name}
                    </div>
                </div>

                <div className="progress-steps">
                    {STEPS.map((s, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                            <div className="step-item" style={{ cursor: 'pointer' }} onClick={() => setStep(i)}>
                                <div className={`step-circle ${i < step ? 'done' : i === step ? 'active' : 'todo'}`}>{i < step ? '✓' : i + 1}</div>
                                <div className={`step-label ${i < step ? 'done' : i === step ? 'active' : ''}`}>{s}</div>
                            </div>
                            {i < STEPS.length - 1 && <div className={`step-line ${i < step ? 'done' : ''}`} />}
                        </div>
                    ))}
                </div>

                {/* ÉTAPE 0 — INFOS */}
                {step === 0 && (
                    <div className="submit-card">
                        <div className="submit-card-title"><span>◉</span> General information</div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Project name <span>*</span></label>
                                <input className="form-input" value={form.name} onChange={e => update('name', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Ticker <span>*</span></label>
                                <input className="form-input" value={form.ticker} onChange={e => update('ticker', e.target.value)} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Type <span>*</span></label>
                                <select className="form-select" value={form.type} onChange={e => update('type', e.target.value)}>
                                    <option value="ico">ICO</option>
                                    <option value="ido">IDO</option>
                                    <option value="nft">NFT</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Blockchain <span>*</span></label>
                                <input className="form-input" value={form.chain} onChange={e => update('chain', e.target.value)} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Category <span>*</span></label>
                                <select className="form-select" value={form.categorie} onChange={e => update('categorie', e.target.value)}>
                                    <option value="">— Select —</option>
                                    {PROJECT_CATEGORIES.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Logo URL <span style={{ color: '#64748b', fontWeight: 400 }}>(Must be hosted on your own domain.)</span></label>
                                <input className="form-input" placeholder="https://yourproject.com/logo.png" value={form.logo_url} onChange={e => update('logo_url', e.target.value)} />
                                {form.logo_url && form.website && (() => {
                                    try {
                                        const logoDomain    = new URL(form.logo_url).hostname.replace(/^www\./, '');
                                        const websiteDomain = new URL(form.website).hostname.replace(/^www\./, '');
                                        if (logoDomain !== websiteDomain) return (
                                            <div style={{ marginTop: '4px', fontSize: '0.75rem', color: '#f87171' }}>
                                                ❌ Logo domain <strong>{logoDomain}</strong> does not match your website domain <strong>{websiteDomain}</strong>.
                                            </div>
                                        );
                                        return <div style={{ marginTop: '4px', fontSize: '0.75rem', color: '#34D399' }}>✓ Domain verified</div>;
                                    } catch { return null; }
                                })()}
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Website <span>*</span></label>
                                <input className="form-input" value={form.website} onChange={e => update('website', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Whitepaper <span>*</span></label>
                                <input className="form-input" value={form.whitepaper} onChange={e => update('whitepaper', e.target.value)} />
                            </div>
                        </div>
                    </div>
                )}

                {/* ÉTAPE 1 — DÉTAILS */}
                {step === 1 && (
                    <div className="submit-card">
                        <div className="submit-card-title"><span>◉</span> Project details</div>
                        <div className="form-group">
                            <label className="form-label">Description <span>*</span></label>
                            <textarea className="form-textarea" value={form.description} onChange={e => update('description', e.target.value)} rows={5} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">YouTube video URL <span style={{ color: '#64748b' }}>(optional)</span></label>
                            <input className="form-input" placeholder="https://www.youtube.com/watch?v=..." value={form.url_video} onChange={e => update('url_video', e.target.value)} />
                            <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '6px' }}>The video will be displayed at the top of your project description.</div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Contract Address <span>*</span></label>
                                <input className="form-input" value={form.contract_address} onChange={e => update('contract_address', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Launch date</label>
                                <input className="form-input" type="date" value={form.launch_date} onChange={e => update('launch_date', e.target.value)} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Presale start</label>
                                <input className="form-input" type="date" value={form.presale_start} onChange={e => update('presale_start', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Presale end</label>
                                <input className="form-input" type="date" value={form.presale_end} onChange={e => update('presale_end', e.target.value)} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">KYC — Company</label>
                                <input className="form-input" value={form.kyc} onChange={e => update('kyc', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">KYC — URL</label>
                                <input className="form-input" value={form.kyc_url} onChange={e => update('kyc_url', e.target.value)} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Audit — Company</label>
                                <input className="form-input" value={form.audit} onChange={e => update('audit', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Audit — URL</label>
                                <input className="form-input" value={form.audit_url} onChange={e => update('audit_url', e.target.value)} />
                            </div>
                        </div>
                    </div>
                )}

                {/* ÉTAPE 2 — TOKENOMICS */}
                {step === 2 && (
                    <div className="submit-card">
                        <div className="submit-card-title"><span>◉</span> Tokenomics</div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Supply total <span>*</span></label>
                                <input className="form-input" value={form.tokenomics['Supply total']} onChange={e => updateTokenomics('Supply total', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Hard Cap <span>*</span></label>
                                <input className="form-input" value={form.tokenomics['Hard Cap']} onChange={e => updateTokenomics('Hard Cap', e.target.value)} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Soft Cap</label>
                                <input className="form-input" value={form.tokenomics['Soft Cap']} onChange={e => updateTokenomics('Soft Cap', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Currency <span>*</span></label>
                                <input className="form-input" value={form.tokenomics.devise} onChange={e => updateTokenomics('devise', e.target.value)} />
                            </div>
                        </div>
                        <div className="form-row single">
                            <div className="form-group">
                                <label className="form-label">Vesting <span>*</span></label>
                                <input className="form-input" value={form.tokenomics['Vesting']} onChange={e => updateTokenomics('Vesting', e.target.value)} />
                            </div>
                        </div>
                        <div style={{ marginBottom: '24px' }}>
                            <div style={{ color: '#94a3b8', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Token price <span style={{ color: '#EC4899' }}>*</span></div>
                            {form.tokenomics.phases.map((phase, i) => (
                                <div className="dynamic-item" key={i} style={{ marginBottom: '16px' }}>
                                    <button className="dynamic-item-remove" onClick={() => removePhase(i)}>✕</button>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label className="form-label">Price <span>*</span></label>
                                            <input className="form-input" value={phase.price} onChange={e => updatePhase(i, 'price', e.target.value)} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Duration / Quantity</label>
                                            <input className="form-input" value={phase.duration} onChange={e => updatePhase(i, 'duration', e.target.value)} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <button className="add-btn" onClick={addPhase}>+ Add a price</button>
                        </div>
                        <div style={{ marginBottom: '24px' }}>
                            <div style={{ color: '#94a3b8', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Token distribution</div>
                            {form.token_distribution.map((d, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: DISTRIB_COLORS[i % DISTRIB_COLORS.length], flexShrink: 0 }} />
                                    <input className="form-input" placeholder="Ex: Presale" value={d.label} onChange={e => updateDistrib(i, 'label', e.target.value)} style={{ flex: 2 }} />
                                    <input className="form-input" placeholder="%" type="number" value={d.percent} onChange={e => updateDistrib(i, 'percent', e.target.value)} style={{ flex: 1 }} />
                                    <button onClick={() => removeDistrib(i)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '1rem', padding: '4px' }}>✕</button>
                                </div>
                            ))}
                            <button className="add-btn" onClick={addDistrib}>+ Add a row</button>
                        </div>
                        <div>
                            <div style={{ color: '#94a3b8', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Roadmap</div>
                            {form.roadmap.map((item, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                    <input className="form-input" value={item} onChange={e => updateRoadmap(i, e.target.value)} style={{ flex: 1 }} />
                                    <button onClick={() => removeRoadmap(i)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '1rem', padding: '4px', flexShrink: 0 }}>✕</button>
                                </div>
                            ))}
                            <button className="add-btn" onClick={addRoadmap}>+ Add a step</button>
                        </div>
                    </div>
                )}

                {/* ÉTAPE 3 — ÉQUIPE & FAQ */}
                {step === 3 && (
                    <div className="submit-card">
                        <div className="submit-card-title"><span>◉</span> Team <span style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: '400' }}>(optional)</span></div>
                        {form.team.map((member, i) => (
                            <div className="dynamic-item" key={i} style={{ marginBottom: '16px' }}>
                                <button className="dynamic-item-remove" onClick={() => removeTeam(i)}>✕</button>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Name</label>
                                        <input className="form-input" value={member.name} onChange={e => updateTeam(i, 'name', e.target.value)} />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Role</label>
                                        <input className="form-input" value={member.role} onChange={e => updateTeam(i, 'role', e.target.value)} />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Bio</label>
                                    <textarea className="form-textarea" value={member.bio} onChange={e => updateTeam(i, 'bio', e.target.value)} rows={2} />
                                </div>
                            </div>
                        ))}
                        <button className="add-btn" onClick={addTeam}>+ Add a member</button>
                        <div style={{ marginTop: '32px' }}>
                            <div className="submit-card-title"><span>◉</span> FAQ <span style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: '400' }}>(optional)</span></div>
                            {form.faq.map((item, i) => (
                                <div className="dynamic-item" key={i} style={{ marginBottom: '16px' }}>
                                    <button className="dynamic-item-remove" onClick={() => removeFaq(i)}>✕</button>
                                    <div className="form-group">
                                        <label className="form-label">Question</label>
                                        <input className="form-input" value={item.q} onChange={e => updateFaq(i, 'q', e.target.value)} />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Answer</label>
                                        <textarea className="form-textarea" value={item.a} onChange={e => updateFaq(i, 'a', e.target.value)} rows={2} />
                                    </div>
                                </div>
                            ))}
                            <button className="add-btn" onClick={addFaq}>+ Add a question</button>
                        </div>
                    </div>
                )}

                {/* ÉTAPE 4 — RÉSEAUX & PUB */}
                {step === 4 && (
                    <div className="submit-card">
                        <div className="submit-card-title"><span>◉</span> Social networks</div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Twitter <span>*</span></label>
                                <input className="form-input" placeholder="https://twitter.com/..." value={form.twitter} onChange={e => update('twitter', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Telegram</label>
                                <input className="form-input" placeholder="https://t.me/..." value={form.telegram} onChange={e => update('telegram', e.target.value)} />
                            </div>
                        </div>
                        <div className="form-row single">
                            <div className="form-group">
                                <label className="form-label">Discord</label>
                                <input className="form-input" placeholder="https://discord.gg/..." value={form.discord} onChange={e => update('discord', e.target.value)} />
                            </div>
                        </div>

                        <div className="submit-card-title" style={{ marginTop: '24px' }}><span>◉</span> Promoted Option <span style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: '400' }}>(optional)</span></div>

                        {/* OFFRES ACTIVES */}
                        {form.active_promos && form.active_promos.filter(p => p.status === 'confirmed').length > 0 && (
                            <div style={{ marginBottom: '16px' }}>
                                {form.active_promos.filter(p => p.status === 'confirmed').map((promo, i) => (
                                    <div key={i} style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: '12px', padding: '12px', marginBottom: '8px' }}>
                                        <div style={{ color: '#34D399', fontWeight: '600', fontSize: '0.85rem' }}>
                                            ✅ {promo.label} — active until {new Date(promo.promo_until).toLocaleDateString('en-GB')}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {settings && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                                {[
                                    { key: 'boost',      icon: '⚡', label: 'Boost',      desc: 'Give your project a push! A boost temporarily improves your ranking position for 48h.', price: `from ${settings.boost_single} $` },
                                    { key: 'banner',     icon: '📢', label: 'Banner',     desc: 'Your advertising banner (300x250px image) is displayed in the right column of other projects\' detail pages.', price: `from ${settings.banner_week} $` },
                                    { key: 'premium',    icon: '⭐', label: 'Premium',    desc: 'Your project is displayed as a priority in the "Featured" section on the homepage.', price: `from ${settings.premium_week} $` },
                                    { key: 'card_image', icon: '🖼️', label: 'Card Image', desc: 'Display a banner image on your card in the ICO list. Unlimited duration — one-time payment.', price: `${settings.card_image} $` },
                                ].map(pkg => {
                                    const active = isPromoActive(pkg.key);
                                    return (
                                        <div key={pkg.key}
                                            onClick={() => { if (!active) { update('promoted_package', pkg.key); update('promoted_duration', ''); } }}
                                            style={{
                                                background: active ? 'rgba(52,211,153,0.05)' : form.promoted_package === pkg.key ? 'rgba(245,158,11,0.08)' : 'rgba(255,255,255,0.02)',
                                                border: `1px solid ${active ? 'rgba(52,211,153,0.2)' : form.promoted_package === pkg.key ? 'rgba(245,158,11,0.4)' : 'rgba(255,255,255,0.07)'}`,
                                                borderRadius: '16px', padding: '16px',
                                                cursor: active ? 'not-allowed' : 'pointer',
                                                transition: 'all 0.2s', display: 'flex', gap: '16px', alignItems: 'flex-start',
                                                opacity: active ? 0.6 : 1
                                            }}
                                        >
                                            <div style={{ fontSize: '1.8rem', flexShrink: 0 }}>{pkg.icon}</div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                    <div style={{ color: '#F1F5F9', fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '1rem' }}>{pkg.label}</div>
                                                    <div style={{ color: active ? '#34D399' : '#F59E0B', fontWeight: '600', fontSize: '0.85rem' }}>{active ? '✅ Active' : pkg.price}</div>
                                                </div>
                                                <div style={{ color: '#94a3b8', fontSize: '0.82rem', lineHeight: '1.6' }}>{pkg.desc}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Card Image — pas de champ URL ici, l'upload se fait depuis Mon Compte après validation admin */}
                        {form.promoted_package === 'card_image' && !isPromoActive('card_image') && settings && (
                            <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '16px', padding: '16px', textAlign: 'center', marginBottom: '16px' }}>
                                <div style={{ color: '#94a3b8', fontSize: '0.82rem', marginBottom: '6px' }}>Total — one-time payment, unlimited duration</div>
                                <div style={{ color: '#F59E0B', fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '1.8rem' }}>{settings.card_image} $</div>
                                <div style={{ color: '#94a3b8', fontSize: '0.78rem', marginTop: '8px' }}>After payment, you can upload your image from <strong style={{ color: '#F1F5F9' }}>My Account</strong> — it will be validated by the admin before going live.</div>
                            </div>
                        )}

                        {/* Autres offres — sélection durée */}
                        {form.promoted_package && form.promoted_package !== 'card_image' && !isPromoActive(form.promoted_package) && settings && (
                            <>
                                <div style={{ color: '#94a3b8', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Duration</div>
                                <div className="duration-options">
                                    {form.promoted_package === 'boost' && (
                                        <>
                                            <button className={`duration-btn${form.promoted_duration === 'boost_single' ? ' selected' : ''}`} onClick={() => update('promoted_duration', 'boost_single')}>x1 — {settings.boost_single} $</button>
                                            <button className={`duration-btn${form.promoted_duration === 'boost_pack10' ? ' selected' : ''}`} onClick={() => update('promoted_duration', 'boost_pack10')}>x10 — {settings.boost_pack10} $</button>
                                        </>
                                    )}
                                    {form.promoted_package === 'banner' && (
                                        <>
                                            <button className={`duration-btn${form.promoted_duration === 'banner_week' ? ' selected' : ''}`} onClick={() => update('promoted_duration', 'banner_week')}>1 week — {settings.banner_week} $</button>
                                            <button className={`duration-btn${form.promoted_duration === 'banner_month' ? ' selected' : ''}`} onClick={() => update('promoted_duration', 'banner_month')}>1 month — {settings.banner_month} $</button>
                                        </>
                                    )}
                                    {form.promoted_package === 'premium' && (
                                        <>
                                            <button className={`duration-btn${form.promoted_duration === 'premium_week' ? ' selected' : ''}`} onClick={() => update('promoted_duration', 'premium_week')}>1 week — {settings.premium_week} $</button>
                                            <button className={`duration-btn${form.promoted_duration === 'premium_month' ? ' selected' : ''}`} onClick={() => update('promoted_duration', 'premium_month')}>1 month — {settings.premium_month} $</button>
                                        </>
                                    )}
                                </div>
                                {form.promoted_duration && (
                                    <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '16px', padding: '16px', textAlign: 'center', marginTop: '16px' }}>
                                        <div style={{ color: '#94a3b8', fontSize: '0.82rem', marginBottom: '6px' }}>Total</div>
                                        <div style={{ color: '#F59E0B', fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '1.8rem' }}>{settings[form.promoted_duration]} $</div>
                                        <div style={{ color: '#94a3b8', fontSize: '0.78rem', marginTop: '8px' }}>Payment after submission via your project page</div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* ÉTAPE 5 — CONFIRMATION */}
                {step === 5 && (
                    <div className="submit-card">
                        <div className="submit-card-title"><span>◉</span> Confirm changes</div>
                        <div style={{ color: '#94a3b8', lineHeight: '1.8', marginBottom: '20px' }}>
                            <p>You are about to save the changes to <strong style={{ color: '#F1F5F9' }}>{form.name}</strong>.</p>
                            <p style={{ marginTop: '12px', color: '#F59E0B', fontSize: '0.9rem' }}>⚠ After modification, your project will go back to pending validation before being republished.</p>
                        </div>
                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label className="form-label">💬 Comment <span>*</span></label>
                            <textarea className="form-textarea" placeholder="Describe the changes made..." value={form.update_comment || ''} onChange={e => update('update_comment', e.target.value)} rows={3} />
                        </div>
                        {error && <div style={{ color: '#f87171', fontSize: '0.85rem', marginBottom: '12px' }}>{error}</div>}
                        <button className="btn-submit" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : '💾 Save changes'}
                        </button>
                    </div>
                )}

                <div className="nav-buttons">
                    {step > 0 ? (
                        <button className="btn-prev" onClick={() => setStep(s => s - 1)}>← Previous</button>
                    ) : (
                        <button className="btn-prev" onClick={() => navigate('/mon-compte')}>← My account</button>
                    )}
                    {step < STEPS.length - 1 && (
                        <button className="btn-next" onClick={() => setStep(s => s + 1)}>Next →</button>
                    )}
                </div>
            </div>
        </div>
    );
}
